// ═══════════════════════════════════════════════════════════════
// ALLOYHEAD WASM BRIDGE
// ═══════════════════════════════════════════════════════════════
// Loads the embedded 6.6KB WASM binary (9 C kernels compiled via
// clang --target=wasm32) and provides runKernel() dispatch.
// Also exports detectCapabilities() for feature detection.
// ═══════════════════════════════════════════════════════════════

import { PIXALLOY_WASM_B64 } from './kernels-wasm-b64.js';

// Decode base64 → Uint8Array
function b64ToUint8(b64) {
  const bin = atob(b64);
  const arr = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
  return arr;
}

/**
 * WasmBridge — loads and manages the PixAlloy WASM kernel module.
 *
 * Usage:
 *   const bridge = new WasmBridge();
 *   await bridge.init();
 *   bridge.runKernel("grayscale", imageData.data, w, h);
 */
export class WasmBridge {
  constructor() {
    this._wasmAvailable = false;
    this._instance = null;
    this._memory = null;
    this._exports = null;
  }

  async init() {
    try {
      const wasmBytes = b64ToUint8(PIXALLOY_WASM_B64);
      const importObject = {
        env: {
          memory: new WebAssembly.Memory({ initial: 64, maximum: 256 }),
        },
      };
      const { instance } = await WebAssembly.instantiate(wasmBytes, importObject);
      this._instance = instance;
      this._exports = instance.exports;
      this._memory = this._exports.memory || importObject.env.memory;
      this._wasmAvailable = true;

      // Call ctors if present
      if (this._exports.__wasm_call_ctors) {
        this._exports.__wasm_call_ctors();
      }
    } catch (err) {
      console.warn("[WasmBridge] WASM init failed, falling back to JS kernels:", err.message);
      this._wasmAvailable = false;
    }
  }

  /**
   * Run a named kernel on pixel data.
   * @param {string} name - Kernel name (e.g. "grayscale", "invert", "brightness_contrast")
   * @param {Uint8ClampedArray} data - RGBA pixel data (modified in place)
   * @param {number} w - Image width
   * @param {number} h - Image height
   * @param {object} [params] - Optional kernel parameters
   */
  runKernel(name, data, w, h, params) {
    if (!this._wasmAvailable || !this._exports[name]) return;

    const byteLen = data.length;
    const pixelCount = w * h * 4;

    // Ensure WASM memory is large enough
    const pages = this._memory.buffer.byteLength / 65536;
    const needed = Math.ceil((pixelCount + 1024) / 65536);
    if (needed > pages) {
      this._memory.grow(needed - pages);
    }

    // Copy pixel data into WASM memory
    const wasmData = new Uint8Array(this._memory.buffer);
    const ptr = 1024; // Start after stack area
    wasmData.set(data, ptr);

    // Call the kernel
    const fn = this._exports[name];
    if (name === "brightness_contrast") {
      fn(ptr, pixelCount, params?.brightness ?? 0, params?.contrast ?? 0);
    } else if (name === "levels") {
      fn(ptr, pixelCount, params?.inBlack ?? 0, params?.inWhite ?? 255, params?.gamma ?? 1.0, params?.outBlack ?? 0, params?.outWhite ?? 255);
    } else if (name === "hue_saturation") {
      fn(ptr, pixelCount, params?.hue ?? 0, params?.saturation ?? 0, params?.lightness ?? 0);
    } else if (name === "gaussian_blur") {
      fn(ptr, w, h, params?.radius ?? 2);
    } else if (name === "sharpen") {
      fn(ptr, w, h);
    } else if (name === "apply_lut") {
      // LUT is a 256-byte array — write it after the pixel data
      const lutPtr = ptr + pixelCount;
      if (params?.lut) wasmData.set(params.lut, lutPtr);
      fn(ptr, pixelCount, lutPtr);
    } else {
      // Simple kernels: invert, grayscale, sepia — take (ptr, byteLen)
      fn(ptr, pixelCount);
    }

    // Copy results back
    data.set(wasmData.subarray(ptr, ptr + byteLen));
  }

  destroy() {
    this._instance = null;
    this._exports = null;
    this._memory = null;
    this._wasmAvailable = false;
  }
}

/**
 * Detect browser capabilities for AlloyHead.
 * Returns { wasm, sharedMemory, worker, offscreenCanvas, webgl2 }
 */
export function detectCapabilities() {
  return {
    wasm: typeof WebAssembly !== "undefined",
    sharedMemory: typeof SharedArrayBuffer !== "undefined",
    worker: typeof Worker !== "undefined",
    offscreenCanvas: typeof OffscreenCanvas !== "undefined",
    webgl2: (() => {
      try {
        const c = document.createElement("canvas");
        return !!c.getContext("webgl2");
      } catch { return false; }
    })(),
  };
}
